import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";

type AppUser = { email?: string; user_metadata?: Record<string, unknown> };
type AppSession = { access_token: string; user: AppUser };

type AuthContextValue = {
  session: AppSession | null;
  user: AppUser | null;
  booting: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const preview = import.meta.env.DEV && new URLSearchParams(window.location.search).get("preview") === "1";
  const [session, setSession] = useState<AppSession | null>(preview ? {
    access_token: "local-visual-preview",
    user: { email: "client@northstarcommerce.com", user_metadata: { business_name: "Northstar Commerce" } },
  } : null);
  const [booting, setBooting] = useState(!preview && Boolean(supabase));

  useEffect(() => {
    if (preview || !supabase) return;
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setBooting(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setBooting(false);
    });
    return () => data.subscription.unsubscribe();
  }, [preview]);

  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    user: session?.user || null,
    booting,
    signOut,
  }), [session, booting, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
